import { useTranslation } from 'react-i18next'
import { HelpHint } from '@/components/common/help/HelpHint'
import { subtypes } from '@/lib/action-modules/registry'
import type { SubtypeDefinition } from '@/lib/action-modules/registry/types'
import { cn } from '@/lib/utils'
import { useDraggable } from '@dnd-kit/core'

export const NEW_SUBTYPE_PREFIX = 'new-'

// Toolbox of draggable subtype chips. Dragging a chip into the preview adds that subtype.
export default function SubtypeToolbox() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-2">
      {subtypes.map(def => (
        <ToolboxEntry key={def.type} definition={def} />
      ))}
    </div>
  )
}

/** The chip's visual. Shared by the toolbox entries and the drag overlay so a dragged chip
 *  looks identical to the one left behind. */
export function ToolboxChip({ definition, dragging }: { definition: SubtypeDefinition; dragging?: boolean }) {
  const { t } = useTranslation('action-modules-wizard')
  return (
    <div
      className={cn(
        // pr-9 keeps the label clear of the overlaid help trigger in the toolbox.
        'cursor-grab select-none rounded-md border bg-card p-3 pr-9 active:cursor-grabbing',
        dragging && 'shadow-lg',
      )}
    >
      {definition.toolboxPlaceholder(t)}
    </div>
  )
}

function ToolboxEntry({ definition }: { definition: SubtypeDefinition }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${NEW_SUBTYPE_PREFIX}${definition.type}`,
    data: { kind: 'new', definition },
  })

  return (
    // The help trigger sits outside the draggable element on purpose: inside it, its pointer
    // events would be consumed by the drag sensor. It overlays the chip so the narrow toolbox
    // column stays one item wide.
    <div className="relative">
      {/* The dragged chip is shown by the shared DragOverlay; dim the source in place. */}
      <div ref={setNodeRef} {...listeners} {...attributes} style={{ opacity: isDragging ? 0.4 : 1 }}>
        <ToolboxChip definition={definition} />
      </div>
      {definition.helpKey && (
        <HelpHint helpKey={definition.helpKey} className="absolute right-2 top-1/2 -translate-y-1/2" />
      )}
    </div>
  )
}
